import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Stack, Menu, MenuItem, Checkbox, ListItemText, Divider, Typography, Dialog, DialogTitle, 
  DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, Box,
  Grid, Chip, Card, CardContent, Modal, ModalTitle, ModalContent
} from '@mui/material';
import { CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import SignalCellular0BarIcon from '@mui/icons-material/SignalCellular0Bar';
import SignalCellular1BarIcon from '@mui/icons-material/SignalCellular1Bar';
import SignalCellular2BarIcon from '@mui/icons-material/SignalCellular2Bar';
import SignalCellular3BarIcon from '@mui/icons-material/SignalCellular3Bar';
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import Battery50Icon from '@mui/icons-material/Battery50';
import Battery80Icon from '@mui/icons-material/Battery80';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import imageCompression from 'browser-image-compression';

import './AdminDashboard.css';

import API_BASE_URL from '../../config';
import { DevicesSharp } from '@mui/icons-material';

const AdminDatabase = () => {

  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [trafficLogs, setTrafficLogs] = useState([]);
  const [trafficLoading, setTrafficLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('-1'); // Default to all users
  const [users, setUsers] = useState([]); // Store users for dropdown
  const [openAddNoteDialog, setOpenAddNoteDialog] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [serviceNotes, setServiceNotes] = useState({
    severity: 'green',
    serviceType: 'inspection',
    performedBy: '',
    description: '',
    serviceDescription: '',
    internalNotes: '',
    images: []
  });
  const [timeRange, setTimeRange] = useState('7');
  const [serviceNotesLoading, setServiceNotesLoading] = useState(true);
  const [showInternalNotes, setShowInternalNotes] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchTrafficLogs();
    fetchServiceNotes();
  }, [selectedUserId, timeRange]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
      }

      const userData = await response.json();
      setUsers(userData || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTrafficLogs = async () => {
    try {
      setTrafficLoading(true);
        const response = await fetch(`${API_BASE_URL}/admin/db/overview?userId=${selectedUserId}&timeRange=${timeRange}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
      }

      const data = await response.json();

      setTrafficLogs(data || {});

    } catch (error) {
      console.error('Error fetching database overview:', error);
    } finally {
      setTrafficLoading(false);
    }
  };

  const fetchServiceNotes = async () => {
    setServiceNotesLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/db/service-notes?userId=${selectedUserId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
      }

      const data = await response.json();

      setServiceNotes(data || []);
    } catch (error) {
      console.error('Error fetching service notes:', error);
    } finally {
      setServiceNotesLoading(false);
    }
  };

  const [searchTerms, setSearchTerms] = useState({
    customer: '',
    name: '',
    type: '',
    location: '',
    direction: '',
    connected: '',
    mode: '',
    signal: '',
    battery: '',
    batterytemp: '',
    lastUpdate: ''
  });

  const [filters, setFilters] = useState({
    customer: [],
    type: [],
    location: [],
    direction: []
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [filterColumn, setFilterColumn] = useState(null);

  const [sorting, setSorting] = useState({
    column: null,
    direction: 'asc'
  });

  const handleFilterClick = (e, column) => {
    setAnchorEl(e.currentTarget);
    setFilterColumn(column);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
    setFilterColumn(null);
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => {
      const current = [...prev[column]];
      if (current.includes(value)) {
        return { ...prev, [column]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [column]: [...current, value] };
      }
    });
  };

  const handleSearch = (column, value) => {
    setSearchTerms(prev => ({ ...prev, [column]: value }));
  };

  const handleSort = (column) => {
    setSorting(prev => {
      if (prev.column === column) {
        return { ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const getFilteredAndSortedDevices = () => {
    let filteredDevices = [...devices];

    // Apply search filters
    Object.keys(searchTerms).forEach(column => {
      if (searchTerms[column]) {
        filteredDevices = filteredDevices.filter(device => 
          String(device[column] || '').toLowerCase().includes(searchTerms[column].toLowerCase())
        );
      }
    });

    // Apply multi-select filters
    Object.keys(filters).forEach(column => {
      if (filters[column].length > 0) {
        filteredDevices = filteredDevices.filter(device => 
          filters[column].includes(device[column])
        );
      }
    });

    // Apply sorting
    if (sorting.column) {
      filteredDevices.sort((a, b) => {
        const aVal = a[sorting.column];
        const bVal = b[sorting.column];
        if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filteredDevices;
  };

  const getSignalIcon = (value) => {
    const bars = Math.max(0, Math.min(5, Math.floor((value + 109) / 12) + 1));

    // Clamp the dBm value between -113 and -51
    const clampedDBm = Math.max(-113, Math.min(value, -51));

    // Calculate the percentage of signal strength
    const signalPercentage = (clampedDBm + 113) / (62) * 100;

    if (signalPercentage >= 80) return <SignalCellular4BarIcon color="success" />;
    if (signalPercentage >= 60) return <SignalCellular3BarIcon color="success" />;
    if (signalPercentage >= 40) return <SignalCellular2BarIcon color="warning" />;
    if (signalPercentage >= 20) return <SignalCellular1BarIcon color="error" />;
    return <SignalCellular0BarIcon color="error" />;
  };

  const getBatteryIcon = (percentage) => {
    if (percentage === -1) return <BatteryAlertIcon color="error" />;
    if (percentage >= 80) return <BatteryFullIcon color="success" />;
    if (percentage >= 50) return <Battery80Icon color="success" />;
    if (percentage >= 20) return <Battery50Icon color="warning" />;
    return <Battery20Icon color="error" />;
  };

  const getTimeSinceLastUpdate = (lastUpdate) => {
    if (!lastUpdate) return { text: 'Unknown', color: '#ffffff' };
    
    try {
      const lastUpdateTime = new Date(lastUpdate);
      const now = new Date();
      
      // Adjust for timezone difference: subtract 10 hours
      const adjustedNow = new Date(now.getTime());
      
      const diffMs = adjustedNow - lastUpdateTime;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      let color;
      if (diffHours <= 24) {
        color = '#4CAF50';
      } else if (diffHours <= 24 * 3) {
        color = '#FF9800';
      } else {
        color = '#F44336';
      }
      
      // Format the time difference
      let text;
      if (diffHours < 1) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        text = `${diffMinutes}m ago`;
      } else if (diffHours < 24) {
        text = `${Math.floor(diffHours)}h ago`;
      } else {
        const diffDays = Math.floor(diffHours / 24);
        text = `${diffDays}d ago`;
      }
      
      return { text, color };
    } catch (error) {
      console.error('Error calculating time difference:', error);
      return { text: 'Invalid', color: '#ffffff' };
    }
  };

  // Calculate totals for summary cards

  const FilterMenu = ({ column }) => {
    const uniqueValues = [...new Set(devices.map(device => device[column]))];
    
    const handleSelectAllClick = (e) => {
      e.stopPropagation();
      if (filters[column].length === uniqueValues.length) {
        setFilters(prev => ({ ...prev, [column]: [] }));
      } else {
        setFilters(prev => ({ ...prev, [column]: uniqueValues }));
      }
    };  
    
    return (
      <Menu
        anchorEl={anchorEl}
        open={filterColumn === column}
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff',
            color: '#000000'
          }
        }}
      >
        <MenuItem key="select-all" onClick={handleSelectAllClick}>
          <Checkbox 
            checked={filters[column].length === uniqueValues.length}
            indeterminate={filters[column].length > 0 && filters[column].length < uniqueValues.length}
            sx={{ color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50' } }}
          />
          <ListItemText primary="Select All" />
        </MenuItem>
        <Divider key="divider" sx={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}/>
          {uniqueValues.map(value => (
            <MenuItem key={`${column}-${value}`} onClick={() => handleFilterChange(column, value)}>
              <Checkbox 
                checked={filters[column].includes(value)}
                sx={{ color: '#4CAF50', '&.Mui-checked': { color: '#4CAF50' } }}
              />
              <ListItemText primary={value} />
            </MenuItem>
          ))}
      </Menu>
    );
  };

  const renderColumnHeader = (label, column, canFilter, canSort) => (
    <TableCell>
      <Stack direction="row" spacing={1} alignItems="center">
        {label}
        {canFilter && (
          <IconButton size="small" onClick={(e) => handleFilterClick(e, column)}>
            <FilterListIcon sx={{ 
              fontSize: 20, 
              color: filters[column] && filters[column].length > 0 ? '#4CAF50' : '#000000' 
            }} />
          </IconButton>
        )}
        {canSort && (
          <IconButton size="small" onClick={() => handleSort(column)}>
            <SortIcon 
              sx={{ 
                fontSize: 20, 
                color: sorting.column === column ? '#4CAF50' : '#000000',
                transform: sorting.column === column && sorting.direction === 'desc' ? 'rotate(180deg)' : 'none'
              }} 
            />
          </IconButton>
        )}
        {canFilter && filters[column] && <FilterMenu column={column} />}
      </Stack>
    </TableCell>
  );

  return (
    <div className="admin-dashboard">
        {/* User Selection Box - Very Top (standalone) */}
        <Box sx={{ 
            width: '100%', 
            mt: 6, 
            mb: 3, 
            p: 3, 
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 300 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Select User:</Typography>
                <FormControl fullWidth>
                    <InputLabel>User</InputLabel>
                    <Select
                    value={selectedUserId}
                    label="User"
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    size="small"
                    >
                    <MenuItem value="-1">All Users</MenuItem>
                    {users.map(user => (
                        <MenuItem key={user.id} value={user.id}>
                        {user.org_name || user.id}
                        </MenuItem>
                    ))}
                    </Select>
                </FormControl>
                </Box>
            </Box>
        </Box>

        {/* Traffic Log Section */}
        <Box sx={{ 
        width: '100%', 
        mb: 4, 
        p: 3, 
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#333', fontWeight: 'bold' }}>
            Overview
        </Typography>

        {/* Summary Cards */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, minWidth: 180, backgroundColor: '#e3f2fd' }}>
            <Typography variant="h6">Traffic Logs</Typography>
            <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
              {trafficLoading ? '...' : (trafficLogs?.traffic_log_rows || 0).toLocaleString()}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 180, backgroundColor: '#fff8e1' }}>
            <Typography variant="h6">Battery BMS Records</Typography>
            <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
              {trafficLoading ? '...' : (trafficLogs?.battery_log_rows || 0).toLocaleString()}
            </Typography>
          </Paper>
        </Box>


      </Box>

      {/* Service Notes Section - Car Service Log Style */}
      <Box sx={{ width: '100%', mb: 4, p: 3, backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#333', fontWeight: 'bold' }}>Service Logs</Typography>
        
        {serviceNotesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : serviceNotes.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">No service logs found.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {[...serviceNotes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((log) => {
              const getSeverityLabel = (severity) => {
                switch(severity) {
                  case 'green': return 'OK';
                  case 'yellow': return 'Warning';
                  case 'red': return 'Critical';
                  default: return 'Unknown';
                }
              };

              return (
                <Grid item xs={12} key={log.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderColor:
                        log.severity === 'green' ? 'success.main' :
                        log.severity === 'yellow' ? 'warning.main' :
                        log.severity === 'red' ? 'error.main' : 'grey.400',
                      borderWidth: 2,
                      borderStyle: 'solid',
                    }}
                  >
                    <CardContent>
                      {/* Header - Device Name + Service Type + Date Only */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'bold' }}>
                            {log.device_name || 'Unknown Device'}
                          </Typography>
                          {/* Service Type with severity background (your request) */}
                          <Chip
                            label={log.serviceType || 'Other Service'}
                            size="small"
                            sx={{
                              backgroundColor:
                                log.severity === 'green' ? 'success.light' :
                                log.severity === 'yellow' ? 'warning.light' :
                                log.severity === 'red' ? 'error.light' : 'grey.300',
                              color: log.severity === 'grey' ? 'grey.800' : 'white',
                              fontWeight: '500'
                            }}
                          />
                        </Box>
                        {/* Date Only - No timestamp */}
                        <Typography variant="body2" color="text.secondary">
                          {new Date(log.created_at).toLocaleDateString('en-AU', { 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 3, p: 2, backgroundColor: '#f3f9ff', borderRadius: '4px' }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 'bold' }}>Device Information</Typography>
                        <Grid container spacing={2}>
                          {/* First row: Location + Device Description (side by side) */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Location</Typography>
                            <Typography variant="body1">
                              {log.street_address || 'No address'}, {log.suburb || 'Unknown'} 
                              {log.direction ? ` (${log.direction})` : ''}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Device Description</Typography>
                            <Typography variant="body1">{log.device_description || 'No description'}</Typography>
                          </Grid>
                          {/* Second row: Installed Date + Service Plan (side by side, same date format) */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Installed Date</Typography>
                            <Typography variant="body1">
                              {log.built_date ? new Date(log.built_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Service Plan Expiry</Typography>
                            <Typography variant="body1">{new Date(log.service_plan).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Service Log Details - ALL SECTIONS ALWAYS VISIBLE even if empty */}
                      <Grid container spacing={2}>
                        {/* Technician - always shown */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>Technician</Typography>
                          <Typography variant="body1">{log.performedBy || 'Unknown'}</Typography>
                        </Grid>
                        {/* Service Description - always shown */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>Service Description</Typography>
                          <Typography variant="body1">{log.description || 'N/A'}</Typography>
                        </Grid>
                        {/* Service Details - always shown */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>Service Details</Typography>
                          <Typography variant="body1">{log.details || 'N/A'}</Typography>
                        </Grid>
                        {/* Internal Notes - always shown, toggleable */}
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Internal Notes</Typography>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => setShowInternalNotes(prev => ({...prev, [log.id]: !prev[log.id]}))}
                              sx={{ fontSize: '0.75rem', py: 0, px: 1 }}
                            >
                              {showInternalNotes[log.id] ? 'Hide' : 'Show'}
                            </Button>
                          </Box>
                          {showInternalNotes[log.id] ? (
                            <Typography variant="body1">{log.notes || 'No internal notes'}</Typography>
                          ) : (
                            <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              ••••••••••••
                            </Typography>
                          )}
                        </Grid>
                        {/* Attached Images - always shown */}
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Attached Photos ({log.images?.length || 0})</Typography>
                          {log.images?.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                              {log.images.map((img) => (
                                <img
                                  key={img.id}
                                  src={img.data}
                                  alt={img.name}
                                  onClick={() => setSelectedImage(img.data)}
                                  style={{ 
                                    maxWidth: '200px', 
                                    maxHeight: '200px', 
                                    objectFit: 'cover', 
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                      transform: 'scale(1.02)',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No photos attached</Typography>
                          )}
                        </Grid>
                      </Grid>

                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
      <Modal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        aria-labelledby="enlarged-image-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2,
          maxWidth: '90vw',
          maxHeight: '90vh',
          borderRadius: '8px'
        }}>
          <img
            src={selectedImage}
            alt="Enlarged photo"
            style={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain'
            }}
          />
          <Button 
            fullWidth 
            variant="contained" 
            onClick={() => setSelectedImage(null)}
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </Box>
</Modal>
    </div>
    
  );
};

export default AdminDatabase;